'use client';

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useSearchParams } from 'next/navigation';

interface ReferralContextType {
  referralCode: string | null;
  setReferralCode: (code: string | null) => void;
  clearReferralCode: () => void;
}

const ReferralContext = createContext<ReferralContextType | undefined>(undefined);

export function ReferralProvider({ children }: { children: ReactNode }) {
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const searchParams = useSearchParams();

  useEffect(() => {
    // Check URL parameters for referral code
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode);
      // Store in sessionStorage to persist across page navigation
      sessionStorage.setItem('referralCode', refCode);
    } else {
      // Check sessionStorage for existing referral code
      const storedCode = sessionStorage.getItem('referralCode');
      if (storedCode) {
        setReferralCode(storedCode);
      }
    }
  }, [searchParams]);

  const clearReferralCode = () => {
    setReferralCode(null);
    sessionStorage.removeItem('referralCode');
  };

  return (
    <ReferralContext.Provider
      value={{
        referralCode,
        setReferralCode,
        clearReferralCode
      }}
    >
      {children}
    </ReferralContext.Provider>
  );
}

export function useReferral() {
  const context = useContext(ReferralContext);
  if (context === undefined) {
    throw new Error('useReferral must be used within a ReferralProvider');
  }
  return context;
}